import axios from "axios";
import React from "react";
import { MutationStatus, useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { create } from "zustand";
import { DropzoneFile, DropzoneFileStatus } from "../../components/Dropzone";
import { getBackendUrl, sleep } from "../../utils";

export enum CustomerSpendUploadStatus {
  Idle = "idle",
  Uploading = "uploading",
  Failure = "failure",
  Success = "success",
}

type CustomerSpendStore = {
  status: CustomerSpendUploadStatus;
  files: DropzoneFile[];
  setStatus: (status: CustomerSpendUploadStatus) => void;
  setFiles: (files: DropzoneFile[]) => void;
  updateFileStateById: (id: string, changes: Partial<DropzoneFile>) => void;
};

const useCustomerSpendStore = create<CustomerSpendStore>((set, get) => ({
  status: CustomerSpendUploadStatus.Idle,
  files: [],

  setStatus: (status) => {
    set({ status });
  },

  setFiles: (files) => {
    set({ files });
  },

  updateFileStateById: (id, changes) => {
    set((state) => ({
      files: state.files.map((file) =>
        file.id === id ? { ...file, ...changes } : file,
      ),
    }));
  },
}));

export const useCustomerSpendUpload = () => {
  const { id } = useParams<{ id: string }>();
  const store = useCustomerSpendStore();

  const mutation = useMutation<void, Error, { files: DropzoneFile[] }>({
    mutationKey: "@supplierEnablement/customerSpend.upload",
    onSuccess: () => {
      store.setStatus(CustomerSpendUploadStatus.Success);
    },
    onError: () => {
      store.setStatus(CustomerSpendUploadStatus.Failure);
    },
    mutationFn: async ({ files }) => {
      await sleep(1000);
      for (const file of files) {
        try {
          store.updateFileStateById(file.id, {
            status: DropzoneFileStatus.Uploading,
          });

          const data = new FormData();
          data.append("file", file.binary);

          await axios({
            method: "post",
            baseURL: getBackendUrl(),
            url: `/versioned/connections/${id}/customer_spend`,
            data,
            onUploadProgress: (progress: ProgressEvent) => {
              const percent = progress.total
                ? Math.round((progress.loaded / progress.total) * 100)
                : 0;

              if (percent > file.progress) {
                store.updateFileStateById(file.id, { progress: percent });
              }
            },
            headers: {
              "X-Rutter-Version": "2023-02-07",
            },
          });

          store.updateFileStateById(file.id, {
            status: DropzoneFileStatus.Success,
          });
        } catch (error) {
          store.updateFileStateById(file.id, {
            status: DropzoneFileStatus.Failure,
          });
        } finally {
          await sleep(1000);
        }
      }
    },
  });

  React.useEffect(() => {
    const mapping: Record<MutationStatus, CustomerSpendUploadStatus> = {
      idle: CustomerSpendUploadStatus.Idle,
      loading: CustomerSpendUploadStatus.Uploading,
      error: CustomerSpendUploadStatus.Failure,
      success: CustomerSpendUploadStatus.Success,
    };

    store.setStatus(mapping[mutation.status]);
  }, [mutation.status]);

  const isLoading = React.useMemo(
    () => [CustomerSpendUploadStatus.Uploading].includes(store.status),
    [store.status],
  );

  return {
    status: store.status,
    isLoading,
    error: mutation.error?.message ?? null,
    files: store.files,
    setFiles: store.setFiles,
    setStatus: store.setStatus,
    submit: () => {
      mutation.mutate({ files: store.files });
    },
    retry: (fileToRetry: DropzoneFile) => {
      mutation.mutate({
        files: store.files.filter((file) => file.id === fileToRetry.id),
      });
    },
  };
};
